import axios from 'axios';
import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Smartphone, Save, RotateCcw } from "lucide-react";
import { toast } from "sonner";

const API_URL = (typeof window !== 'undefined' ? window.__API_URL__ : process.env.REACT_APP_BACKEND_URL) || '';

export default function ConfigurationMobile() {
  const [config, setConfig] = useState({});
  const [roles, setRoles] = useState([]);
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API_URL}/api/configuration-mobile`)
      .then(r => r.data)
      .then(d => {
        setConfig(d.config || {});
        setRoles(d.roles || []);
        setPages(d.pages_disponibles || []);
      })
      .catch(() => toast.error("Erreur chargement"))
      .finally(() => setLoading(false));
  }, []);

  const updateBar = (role, idx, pageKey) => {
    setConfig(prev => {
      const updated = { ...prev };
      const bar = [...(updated[role]?.bar || [])];
      bar[idx] = pageKey;
      updated[role] = { ...updated[role], bar };
      return updated;
    });
  };

  const updateHome = (role, path) => {
    setConfig(prev => ({
      ...prev,
      [role]: { ...prev[role], home: path }
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const r = await axios.put(`${API_URL}/api/configuration-mobile`, config);
      if (r.status === 200) toast.success("Configuration mobile sauvegardée !");
      else toast.error("Erreur sauvegarde");
    } catch { toast.error("Erreur réseau"); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" /></div>;

  const roleColors = {
    proprietaire: "bg-amber-100 text-amber-800 border-amber-200",
    representante: "bg-purple-100 text-purple-800 border-purple-200",
    charge_projet: "bg-pink-100 text-pink-800 border-pink-200",
    adjoint_admin: "bg-slate-100 text-slate-800 border-slate-200",
    chef_equipe: "bg-blue-100 text-blue-800 border-blue-200",
    peintre: "bg-emerald-100 text-emerald-800 border-emerald-200",
    installateur: "bg-indigo-100 text-indigo-800 border-indigo-200",
  };

  return (
    <div className="space-y-6 max-w-5xl mx-auto" data-testid="config-mobile-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Smartphone className="w-6 h-6 text-blue-600" />
            Configuration Mobile
          </h1>
          <p className="text-slate-500 mt-1 text-sm">Choisissez ce que chaque rôle voit sur mobile : page d'accueil et boutons raccourcis</p>
        </div>
        <Button onClick={handleSave} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700">
          <Save className="w-4 h-4 mr-2" />
          {saving ? "Sauvegarde..." : "Sauvegarder"}
        </Button>
      </div>

      {roles.map(role => {
        const rc = config[role.key] || { home: "/mon-espace", bar: [] };
        const barItems = rc.bar || [];
        const color = roleColors[role.key] || "bg-slate-100 text-slate-800";

        return (
          <Card key={role.key} className={`border-l-4 ${color.split(' ')[0].replace('bg-', 'border-')}`}>
            <CardHeader className="pb-3">
              <div className="flex items-center gap-3">
                <Badge className={`${color} text-sm px-3 py-1`}>{role.label}</Badge>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Page d'accueil */}
              <div className="flex items-center gap-3">
                <span className="text-sm font-medium text-slate-700 w-32 shrink-0">Page d'accueil :</span>
                <Select value={rc.home || ""} onValueChange={(v) => updateHome(role.key, v)}>
                  <SelectTrigger className="w-56"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {pages.filter(p => p.path && !p.path.startsWith("__")).map(p => (
                      <SelectItem key={p.key} value={p.path}>{p.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Barre du bas */}
              <div>
                <p className="text-sm font-medium text-slate-700 mb-2">Barre du bas (5 boutons max) :</p>
                <div className="flex gap-2 flex-wrap">
                  {[0, 1, 2, 3, 4].map(idx => (
                    <div key={idx} className="flex flex-col items-center gap-1">
                      <span className="text-[10px] text-slate-400">Bouton {idx + 1}</span>
                      <Select value={barItems[idx] || "aucun"} onValueChange={(v) => updateBar(role.key, idx, v)}>
                        <SelectTrigger className="w-32 h-9 text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {pages.map(p => (
                            <SelectItem key={p.key} value={p.key}>{p.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  ))}
                </div>
              </div>

              {/* Aperçu visuel */}
              <div className="bg-slate-50 rounded-xl p-3 border">
                <p className="text-[10px] text-slate-400 uppercase tracking-wide mb-2">Aperçu barre du bas</p>
                <div className="flex justify-around bg-white rounded-lg border p-2">
                  {barItems.filter(b => b && b !== 'aucun').map((key, i) => {
                    const pg = pages.find(p => p.key === key);
                    return (
                      <div key={i} className="text-center">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold mx-auto ${key === 'pointer' ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-600'}`}>
                          {(pg?.label || '?')[0]}
                        </div>
                        <p className="text-[10px] text-slate-500 mt-0.5">{pg?.label || key}</p>
                      </div>
                    );
                  })}
                </div>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
